/**
 * Pure fiscal calculation helpers shared by the project financial form and the
 * beneficiary fiscal-quota wizards (music + film).
 *
 * No React hooks — safe to import anywhere.
 *
 * Conventions:
 * - All numeric inputs and outputs are string representations of decimal numbers
 *   (matching the wire format used by the backend Decimal fields).
 * - Empty strings, null, undefined, non-numeric values are treated as 0.
 * - Functions return "" when the computed value is 0 to keep input fields empty.
 */

interface SubsidyEntry {
  title: string;
  amount: string;
}

/**
 * Half a cent — the largest tolerance that can never cross a real monetary
 * boundary (smallest representable difference is 0.01 €) but absorbs IEEE-754
 * accumulation noise from sums like 1500.30 + 200.10 = 1700.4000000000001.
 */
const MONEY_EPSILON = 0.005;

/**
 * Art. 36.3 LIS: the generated deduction cannot exceed 500.000 € per taxpayer
 * and tax period. Applied per quota (music quotas are one per fiscal year).
 */
const MUSIC_DEDUCTION_CAP_EUR = 500_000;

/**
 * Art. 36.1 LIS: the film deduction cannot exceed 20.000.000 € per production.
 */
const FILM_DEDUCTION_CAP_EUR = 20_000_000;

/**
 * Art. 36.1 LIS (estatal): 30% applies to the first 1M€ of base, 25% above it.
 * We don't compute brackets (foral/REF rates are asesor-owned); we only warn
 * when a flat estatal rate is applied over this threshold.
 */
const FILM_BRACKET_THRESHOLD_EUR = 1_000_000;
const FILM_ESTATAL_RATE_PCT = 30;

/**
 * Aid-intensity ceilings (deducción + ayudas over total cost): Art. 36.1 film
 * 50% (with casuistic elevations we don't model), Art. 36.3 music 80%.
 */
const FILM_AID_INTENSITY_LIMIT = 0.5;
const MUSIC_AID_INTENSITY_LIMIT = 0.8;

/**
 * Art. 36.1 LIS: copies/publicity/promotion enter the base "con el límite
 * para ambos del 40% del coste de producción".
 */
const FILM_PROMO_LIMIT_RATIO = 0.4;

/**
 * Art. 39.7 LIS: the financier's deduction cannot exceed 1.20x the amounts
 * contributed → investor profitability is capped at 20%.
 */
export const MAX_PROFITABILITY_PCT = 20;

export const DEFAULT_PROFITABILITY_PCT = "20";

/**
 * Art. 39.7 LIS: the agreed investor profitability cannot exceed 20%. An empty
 * value is treated as within the cap (the field hasn't been filled yet).
 */
export function isWithinProfitabilityCap(profitabilityPct: string | null | undefined): boolean {
  if (!profitabilityPct) return true;
  const pct = parseFloat(profitabilityPct);
  if (!Number.isFinite(pct)) return true;
  return pct <= MAX_PROFITABILITY_PCT;
}

function parseAmount(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sumSubsidies(subsidies: readonly SubsidyEntry[] | null | undefined): number {
  if (!subsidies || subsidies.length === 0) return 0;
  return subsidies.reduce((sum, subsidy) => sum + parseAmount(subsidy.amount), 0);
}

/** Sum the cost-breakdown categories into a total eligible cost string. */
export function calculateTotalEligibleCost(categoryAmounts: readonly string[]): string {
  const sum = categoryAmounts.reduce<number>((acc, value) => acc + parseAmount(value), 0);
  return sum > 0 ? sum.toFixed(2) : "";
}

/**
 * Calculate the deduction base: total_eligible_cost - sum(subsidies).
 *
 * @param totalEligibleCost  Total eligible cost as string
 * @param hasPublicSubsidies Whether subsidies apply (false => returns eligibleCost)
 * @param subsidies          Subsidy entries to subtract when hasPublicSubsidies is true
 */
export function calculateDeductionBase(
  totalEligibleCost: string,
  hasPublicSubsidies: boolean,
  subsidies: readonly SubsidyEntry[] | null | undefined,
): number {
  const eligibleCost = parseAmount(totalEligibleCost);
  if (!hasPublicSubsidies || !subsidies || subsidies.length === 0) {
    return eligibleCost;
  }
  return Math.max(0, eligibleCost - sumSubsidies(subsidies));
}

/**
 * Calculate the deduction for performing_arts_music (Art. 36.3 LIS): 20% of
 * the deduction base (eligible - subsidies), clamped to the 500.000 € legal
 * maximum per taxpayer and tax period.
 *
 * Returns "" when the computed deduction is <= 0.
 */
export function calculatePerformingArtsDeduction(deductionBase: string): string {
  const autoDeduction = Math.min(parseAmount(deductionBase) * 0.2, MUSIC_DEDUCTION_CAP_EUR);
  return autoDeduction > 0 ? autoDeduction.toFixed(2) : "";
}

/**
 * Largest investment a deduction can absorb at the given profitability
 * (Art. 39.7 LIS): deduction / (1 + profitability/100).
 *
 * Returns null when either input is missing or non-positive — an unknown
 * profitability means no investment capacity can be promised yet.
 */
export function calculateMaxInvestment(
  deduction: string | null | undefined,
  profitabilityPct: string | null | undefined,
): number | null {
  const deductionNum = parseAmount(deduction ?? "");
  const profitability = parseAmount(profitabilityPct ?? "");
  if (deductionNum <= 0 || profitability <= 0) return null;
  return deductionNum / (1 + profitability / 100);
}

/**
 * Calculate deduction for the general flat-rate case (used for film: 30%).
 * Returns "" when the computed deduction is <= 0.
 */
export function calculateFlatRateDeduction(deductionBase: string, ratePercentage: number): string {
  const base = parseAmount(deductionBase);
  const deduction = base * (ratePercentage / 100);
  return deduction > 0 ? deduction.toFixed(2) : "";
}

/**
 * Calculate the film deduction (Art. 36.1 LIS): deduction base × the quota's
 * rate, clamped to the 20.000.000 € legal maximum per production.
 *
 * Returns "" when the computed deduction is <= 0.
 */
export function calculateFilmDeduction(deductionBase: string, ratePercentage: number): string {
  const deduction = parseAmount(calculateFlatRateDeduction(deductionBase, ratePercentage));
  const clamped = Math.min(deduction, FILM_DEDUCTION_CAP_EUR);
  return clamped > 0 ? clamped.toFixed(2) : "";
}

/**
 * Own contribution = total_budget - sum(subsidies) - max_investment.
 *
 * Kept as a pure helper; empty or zero inputs short-circuit to "".
 */
export function calculateOwnContribution(params: {
  totalBudget: string;
  subsidies: readonly SubsidyEntry[] | null | undefined;
  maxInvestment: string;
}): string {
  const budget = parseAmount(params.totalBudget);
  if (budget <= 0) return "";
  const subsidiesTotal = sumSubsidies(params.subsidies);
  const maxInvestment = parseAmount(params.maxInvestment);
  const own = budget - subsidiesTotal - maxInvestment;
  return own > 0 ? own.toFixed(2) : "0";
}

/** True when deduction amount exceeds the deduction base. */
export function isDeductionExceedsBase(params: {
  deductionBase: string;
  totalGeneratedDeduction: string;
}): boolean {
  const base = parseAmount(params.deductionBase);
  const deduction = parseAmount(params.totalGeneratedDeduction);
  return deduction > 0 && base > 0 && deduction - base > MONEY_EPSILON;
}

/** True when total_budget differs from total_eligible_cost (warning state). */
export function isBudgetNotEqualToEligible(params: {
  totalBudget: string;
  totalEligibleCost: string;
}): boolean {
  const budget = parseAmount(params.totalBudget);
  const eligible = parseAmount(params.totalEligibleCost);
  return budget > 0 && eligible > 0 && Math.abs(budget - eligible) > MONEY_EPSILON;
}

type BreakdownLike = Partial<
  Record<
    "artistic" | "technical" | "promotional" | "production" | "other_non_deductible",
    number | string | null | undefined
  >
>;

type IncentiveLike = "performing_arts_music" | "film_audiovisual";

/**
 * Buckets that enter the Art. 36 LIS deduction base, per incentive type.
 * Mirror of the backend profiles in `app/domain/fiscal/profiles.py`.
 */
const ELIGIBLE_BUCKETS: Record<IncentiveLike, readonly (keyof BreakdownLike)[]> = {
  performing_arts_music: ["artistic", "technical", "promotional"],
  film_audiovisual: ["production", "promotional"],
};

function bucketAmount(
  breakdown: BreakdownLike | null | undefined,
  key: keyof BreakdownLike,
): string {
  if (!breakdown) return "";
  const raw = breakdown[key];
  if (raw === null || raw === undefined || raw === "") return "";
  return String(raw);
}

function eligibleBucketAmounts(
  breakdown: BreakdownLike | null | undefined,
  incentiveType: IncentiveLike,
): string[] {
  return ELIGIBLE_BUCKETS[incentiveType].map((key) => bucketAmount(breakdown, key));
}

/**
 * High-level wrapper that derives the generated deduction from a bucket
 * breakdown using the wizard formulas. Used by the consolidation flow so
 * estimated and consolidated land in the same units without duplicating the
 * formula in two places.
 *
 * Returns "" when the inputs collapse to zero.
 */
export function computeGeneratedDeductionFromBreakdown(params: {
  breakdown: BreakdownLike | null | undefined;
  incentiveType: IncentiveLike | null;
  subsidies: readonly SubsidyEntry[] | null | undefined;
  /** Quota deduction_rate as percentage (20.00 / 30.00). Music ignored. */
  deductionRatePct: string | number | null | undefined;
}): string {
  const { breakdown, incentiveType, subsidies, deductionRatePct } = params;
  if (!breakdown || !incentiveType) return "";

  const totalEligibleCost = calculateTotalEligibleCost(
    eligibleBucketAmounts(breakdown, incentiveType),
  );
  const hasPublicSubsidies = !!subsidies && subsidies.length > 0;
  const base = calculateDeductionBase(totalEligibleCost, hasPublicSubsidies, subsidies);
  if (base <= 0) return "";

  if (incentiveType === "performing_arts_music") {
    return calculatePerformingArtsDeduction(base.toString());
  }
  const rate = parseAmount(
    deductionRatePct === null || deductionRatePct === undefined ? "" : String(deductionRatePct),
  );
  return calculateFilmDeduction(base.toString(), rate);
}

/**
 * ---------------------------------------------------------------------------
 * Single-pass derivation
 *
 * `deriveBudget` is the FE source of truth that composes the per-field helpers
 * above into one pure transformation: raw inputs → derived figures + warnings.
 *
 * The backend mirrors this in `FiscalQuota.recalculate_financials` and is the
 * authoritative writer on persist — the FE result is for live preview only.
 * Keep the two formulas in lockstep.
 * ---------------------------------------------------------------------------
 */

export interface BudgetRawInputs {
  totalBudget: string;
  costBreakdown: Record<string, string | undefined>;
  subsidies: readonly SubsidyEntry[] | null | undefined;
  incentiveType: IncentiveLike;
  /** 30 for state-level film, 20 for music. Music ignores this and uses 20%. */
  deductionRatePct: number;
  /** Empty/undefined when the user hasn't filled it yet (create-wizard budget step). */
  profitabilityPct: string | null | undefined;
}

export interface BudgetDerived {
  totalEligibleCost: string;
  /** Deductible cost (base of the deduction): eligible cost minus subsidies. */
  deductionBase: string;
  totalGeneratedDeduction: string;
  /** Empty when profitability is missing — the field is hidden in that case. */
  ownContribution: string;
  warnings: {
    budgetMismatch: boolean;
    deductionExceedsBase: boolean;
    /** True when the deduction hit its legal cap (500k music / 20M film). */
    deductionCapped: boolean;
    /** Film with the flat 30% estatal rate over a base > 1M€ — the estatal
     * regime applies 30%/25% brackets; the asesor owns the final rate. */
    filmFlatRateOverBracketThreshold: boolean;
    /** Film promotional+copies spend over 40% of production cost (Art. 36.1 LIS). */
    filmPromotionalExceeds40: boolean;
    /** Deducción + ayudas over the aid-intensity ceiling (50% film / 80% music).
     * Only evaluated when there are subsidies — casuistic elevations and
     * foral regimes are the asesor's call. */
    aidIntensityExceeded: boolean;
  };
}

export function deriveBudget(inputs: BudgetRawInputs): BudgetDerived {
  const {
    totalBudget,
    costBreakdown,
    subsidies,
    incentiveType,
    deductionRatePct,
    profitabilityPct,
  } = inputs;

  const isMusic = incentiveType === "performing_arts_music";
  const totalEligibleCost = calculateTotalEligibleCost(
    eligibleBucketAmounts(costBreakdown, incentiveType),
  );

  const hasPublicSubsidies = !!subsidies && subsidies.length > 0;
  const deductibleCostNum = Math.max(
    0,
    calculateDeductionBase(totalEligibleCost, hasPublicSubsidies, subsidies),
  );
  const deductionBase = deductibleCostNum > 0 ? deductibleCostNum.toFixed(2) : "";

  let totalGeneratedDeduction = "";
  if (deductibleCostNum > 0) {
    totalGeneratedDeduction = isMusic
      ? calculatePerformingArtsDeduction(deductionBase)
      : calculateFilmDeduction(deductionBase, deductionRatePct);
  }

  const profitability = parseAmount(profitabilityPct ?? "");
  let ownContribution = "";
  if (profitabilityPct !== null && profitabilityPct !== undefined && profitabilityPct !== "") {
    const factor = 1 + profitability / 100;
    const deduction = parseAmount(totalGeneratedDeduction);
    const maxInvestment = factor > 0 ? (deduction / factor).toFixed(2) : "0";
    ownContribution = calculateOwnContribution({
      totalBudget,
      subsidies,
      maxInvestment,
    });
  }

  const production = parseAmount(costBreakdown.production ?? "");
  const promotional = parseAmount(costBreakdown.promotional ?? "");
  const subsidiesTotal = sumSubsidies(subsidies);
  const deductionNum = parseAmount(totalGeneratedDeduction);
  const eligibleNum = parseAmount(totalEligibleCost);
  const uncappedDeduction = isMusic
    ? deductibleCostNum * 0.2
    : deductibleCostNum * (deductionRatePct / 100);
  const intensityLimit = isMusic ? MUSIC_AID_INTENSITY_LIMIT : FILM_AID_INTENSITY_LIMIT;

  return {
    totalEligibleCost,
    deductionBase,
    totalGeneratedDeduction,
    ownContribution,
    warnings: {
      budgetMismatch: isBudgetNotEqualToEligible({
        totalBudget,
        totalEligibleCost,
      }),
      deductionExceedsBase: isDeductionExceedsBase({
        deductionBase,
        totalGeneratedDeduction,
      }),
      deductionCapped:
        uncappedDeduction - (isMusic ? MUSIC_DEDUCTION_CAP_EUR : FILM_DEDUCTION_CAP_EUR) >
        MONEY_EPSILON,
      filmFlatRateOverBracketThreshold:
        !isMusic &&
        deductionRatePct === FILM_ESTATAL_RATE_PCT &&
        deductibleCostNum - FILM_BRACKET_THRESHOLD_EUR > MONEY_EPSILON,
      filmPromotionalExceeds40:
        !isMusic &&
        promotional > 0 &&
        promotional - production * FILM_PROMO_LIMIT_RATIO > MONEY_EPSILON,
      aidIntensityExceeded:
        subsidiesTotal > 0 &&
        eligibleNum > 0 &&
        subsidiesTotal + deductionNum - eligibleNum * intensityLimit > MONEY_EPSILON,
    },
  };
}

interface Exact {
  units: bigint;
  scale: bigint;
}

const DECIMAL = /^\s*(-)?(\d*)(?:\.(\d*))?\s*$/;
const TEN = 10n;
const HUNDRED = 100n;

function exact(value: string | null | undefined): Exact | null {
  const match = DECIMAL.exec(value ?? "");
  if (!match || (!match[2] && !match[3])) return null;
  const decimals = match[3] ?? "";
  const units = BigInt(`${match[2] || "0"}${decimals}`);
  return { units: match[1] ? -units : units, scale: TEN ** BigInt(decimals.length) };
}

function roundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const rounded = (2n * n + d) / (2n * d);
  return negative ? -rounded : rounded;
}

function roundFloor(numerator: bigint, denominator: bigint): bigint {
  const quotient = numerator / denominator;
  const inexact = quotient * denominator !== numerator;
  return inexact && numerator < 0n !== denominator < 0n ? quotient - 1n : quotient;
}

function toCents(value: string | null | undefined): bigint | null {
  const parsed = exact(value);
  return parsed && roundHalfUp(parsed.units * HUNDRED, parsed.scale);
}

function fromCents(cents: bigint): string {
  const sign = cents < 0n ? "-" : "";
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / HUNDRED}.${(absolute % HUNDRED).toString().padStart(2, "0")}`;
}

function returnFactor(returnPercentage: string | null | undefined): Exact | null {
  const r = exact(returnPercentage);
  return r && { units: HUNDRED * r.scale + r.units, scale: HUNDRED * r.scale };
}

export function deductionOf(amount: string, returnPercentage: string): string | null {
  const a = exact(amount);
  const factor = returnFactor(returnPercentage);
  if (!a || !factor) return null;
  return fromCents(roundHalfUp(a.units * factor.units * HUNDRED, a.scale * factor.scale));
}

export function maxInvestment(deduction: string, returnPercentage: string): string | null {
  const d = exact(deduction);
  const factor = returnFactor(returnPercentage);
  if (!d || !factor || factor.units <= 0n) return null;
  return fromCents(roundFloor(d.units * factor.scale * HUNDRED, d.scale * factor.units));
}

function isPayableReturn(returnPercentage: string): boolean {
  const r = exact(returnPercentage);
  return r !== null && r.units > 0n && r.units <= BigInt(MAX_PROFITABILITY_PCT) * r.scale;
}

export function investmentRoom(
  deductionAvailable: string,
  returnPercentage: string,
  ownDeduction = "0",
): string | null {
  const available = toCents(deductionAvailable);
  const own = toCents(ownDeduction);
  if (available === null || own === null || !isPayableReturn(returnPercentage)) return null;
  return maxInvestment(fromCents(available + own), returnPercentage);
}

export function investmentLeft(room: string, amount: string): string | null {
  const roomCents = toCents(room);
  const amountCents = toCents(amount);
  if (roomCents === null || amountCents === null) return null;
  return fromCents(roomCents - amountCents);
}

export function investmentCapacityAt(completed: string, reserved: string, room: string): string {
  return fromCents((toCents(completed) ?? 0n) + (toCents(reserved) ?? 0n) + (toCents(room) ?? 0n));
}

export function exceedsAmount(amount: string, limit: string | null): boolean {
  const amountCents = toCents(amount);
  const limitCents = limit === null ? null : toCents(limit);
  return amountCents !== null && limitCents !== null && amountCents > limitCents;
}

export function investorMusicDeductionAfter(
  servedMusicDeduction: string,
  amount: string,
  returnPercentage: string,
  replacedDeduction = "0",
): string | null {
  const served = toCents(servedMusicDeduction);
  const replaced = toCents(replacedDeduction);
  const own = deductionOf(amount, returnPercentage);
  if (served === null || replaced === null || own === null) return null;
  return fromCents(served - replaced + (toCents(own) ?? 0n));
}

export const INVESTOR_MUSIC_DEDUCTION_CAP = fromCents(BigInt(MUSIC_DEDUCTION_CAP_EUR) * HUNDRED);
